import { useRecoilCallback } from 'recoil';
import {
    domainEventBus,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
    type DomainEvent,
} from '~/domain/events';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    autoShownRoiIdState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';

function publish(event: DomainEvent, failure: string) {
    domainEventBus.publish(event).catch((error) => {
        console.error(`${failure}: ${error}`);
    });
}

/**
 * Selects a structure. The previous selection is hidden again if selecting
 * it was all that showed it. A hidden structure is shown, and marked as shown
 * only for this selection. Selecting the selected structure leaves it
 * selected.
 */
export default function useRoiSelect(): (roi: RoiReadModel) => void {
    return useRecoilCallback(
        ({ snapshot, set }) =>
            (roi: RoiReadModel) => {
                const selected = snapshot
                    .getLoadable(selectedROIState)
                    .getValue();
                if (selected === roi.roiId) {
                    return;
                }
                if (
                    selected !== null &&
                    snapshot.getLoadable(autoShownRoiIdState).getValue() ===
                        selected
                ) {
                    publish(
                        new RoiVisibilityChangeRequestedEvent(
                            selected,
                            false,
                            'user'
                        ),
                        'Error updating ROI visibility'
                    );
                }
                if (!roi.visible) {
                    publish(
                        new RoiVisibilityChangeRequestedEvent(
                            roi.roiId,
                            true,
                            'user'
                        ),
                        'Error updating ROI visibility'
                    );
                }
                set(autoShownRoiIdState, roi.visible ? null : roi.roiId);
                publish(
                    new RoiSelectionRequestedEvent(roi.roiId, 'user'),
                    'Error selecting ROI'
                );
            },
        []
    );
}
